import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
export function HeroTestimonialsCarousel() {
  const testimonials = [{
    name: 'Profe Mariana Aguirre',
    location: 'Rosario, Santa Fe',
    avatar: 'https://randomuser.me/api/portraits/women/63.jpg',
    text: 'Para quienes todavía dudan: el manual te entrega material listo para dar clases. Abarca absolutamente todos los temas, con la dosis justa de teoría y acompañado de clases armadas, diapositivas, trabajos prácticos y evaluaciones. Es realmente muy completo — me salvó las papas más de una vez.'
  }, {
    name: 'Profesora Daniela Ríos',
    location: 'CABA',
    avatar: 'https://randomuser.me/api/portraits/women/29.jpg',
    text: 'Lo descargué un domingo a la noche y el lunes ya estaba dando clase con las diapositivas. No exagero: me ahorré literalmente toda una semana de preparación. Las actividades son modernas y los chicos enganchan mucho más.'
  }, {
    name: 'Profe Gabriela Méndez',
    location: 'Mendoza',
    avatar: 'https://randomuser.me/api/portraits/women/48.jpg',
    text: 'Soy docente hace 14 años y nunca había encontrado un material tan bien organizado. La progresión de 1° a 6° está pensada en serio, con coherencia pedagógica. Las evaluaciones y los TPs ya están listos, solo los adapto y listo. Una inversión que se paga sola.'
  }];
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setIndex(prev => (prev + newDirection + testimonials.length) % testimonials.length);
  };
  // Autoplay
  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setIndex(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      paginate(diff > 0 ? 1 : -1);
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };
  const variants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -300 : 300,
      opacity: 0
    })
  };
  const current = testimonials[index];
  return <section className="bg-[#FAF7F2] md:pb-16 pb-[20px]" data-id="element-125">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-126">
        <p className="text-center text-xs md:text-sm font-semibold uppercase tracking-wider text-[#1F3A5F]/60 mb-5" data-id="element-127">
          Lo que dicen otras docentes
        </p>

        <div className="relative" data-id="element-128">
          {/* Card */}
          <div className="relative overflow-hidden bg-white rounded-2xl border border-gray-100 shadow-md min-h-[260px] md:min-h-[230px]" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd} data-id="element-129">
            <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-130">
              <motion.div key={index} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
              x: {
                type: 'spring',
                stiffness: 280,
                damping: 30
              },
              opacity: {
                duration: 0.25
              }
            }} className="p-6 md:p-8" data-id="element-131">
                <div className="flex items-center gap-4 mb-4" data-id="element-132">
                  <img src={current.avatar} alt={current.name} className="w-14 h-14 rounded-full object-cover border-2 border-[#1F3A5F] p-0.5 bg-white" data-id="element-133" />
                  <div data-id="element-134">
                    <p className="font-bold text-[#1F3A5F] text-sm md:text-base leading-tight" data-id="element-135">
                      {current.name}
                    </p>
                    <p className="text-gray-500 text-xs md:text-sm mt-0.5" data-id="element-136">
                      {current.location}
                    </p>
                    <div className="flex gap-0.5 mt-1" aria-label="5 estrellas" data-id="element-137">
                      {[...Array(5)].map((_, i) => <Star key={i} size={12} className="fill-[#F59E0B] text-[#F59E0B]" data-id="element-138" />)}
                    </div>
                  </div>
                </div>
                <p className="text-gray-700 italic text-sm md:text-base leading-relaxed font-['Open_Sans',_sans-serif]" data-id="element-139">
                  "{current.text}"
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Arrows (desktop) */}
          <button onClick={() => paginate(-1)} aria-label="Anterior" className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md border border-gray-100 items-center justify-center text-[#1F3A5F] hover:bg-[#1F3A5F] hover:text-white transition-colors" data-id="element-140">
            <ChevronLeft size={20} data-id="element-141" />
          </button>
          <button onClick={() => paginate(1)} aria-label="Siguiente" className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-md border border-gray-100 items-center justify-center text-[#1F3A5F] hover:bg-[#1F3A5F] hover:text-white transition-colors" data-id="element-142">
            <ChevronRight size={20} data-id="element-143" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-5" data-id="element-144">
          {testimonials.map((_, i) => <button key={i} aria-label={`Ir al testimonio ${i + 1}`} onClick={() => {
          setDirection(i > index ? 1 : -1);
          setIndex(i);
        }} className={`h-2 rounded-full transition-all ${i === index ? 'w-8 bg-[#1F3A5F]' : 'w-2 bg-[#1F3A5F]/25'}`} data-id="element-145" />)}
        </div>

        <p className="text-center text-xs text-gray-400 mt-3 md:hidden" data-id="element-146">
          Deslizá para ver más →
        </p>
      </div>
    </section>;
}